import type {
  ModelAssetFile,
  ModelAssetNode,
  ModelAssetReader,
} from '@puppycad/cad-core'
import type { InsertedCadKind } from './insert-cad-kind'
import { normalizeInsertedCadFileId } from './insert-cad-kind'

const RUNTIME_MANIFEST_FILE_ID = 'runtime_manifest.json'
const ASSEMBLY_MANIFEST_KIND = '.json'
const NODE_PACKAGE_KIND = '.puppy'
const EMPTY_REPOSITORY_ID = ''
const EMPTY_BRANCH_ID = ''
const EMPTY_COMMIT_ID = ''
const IDENTITY_TRANSFORM = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
] as const

interface ParseInsertedCadRuntimePackageInput {
  readonly bytes: Uint8Array
  readonly displayName: string
  readonly fileId: string
  readonly kind: InsertedCadKind
}

export interface ParsedInsertedCadRuntimePackage {
  readonly root: ModelAssetNode
  readonly reader: ModelAssetReader
}

interface RuntimeManifestJson {
  readonly root_assembly?: unknown
}

interface AssemblyDocumentJson {
  readonly node_id?: unknown
  readonly product_name?: unknown
  readonly children?: unknown
}

interface AssemblyChildJson {
  readonly instance_id?: unknown
  readonly instance_name?: unknown
  readonly node_id?: unknown
  readonly node_type?: unknown
  readonly transform?: unknown
  readonly nested_assembly_json?: unknown
  readonly node_where?: unknown
  readonly debug?: unknown
  readonly children?: unknown
}

interface RuntimePackageMappingContext {
  readonly entries: ReadonlyMap<string, Uint8Array>
  readonly sourceFileId: string
}

type RuntimePackageReferenceRoot = 'assemblies' | 'parts'

interface BuildChildNodeInput {
  readonly child: AssemblyChildJson
  readonly childIndex: number
  readonly currentAssemblyFileId: string
  readonly parentNodeKey: string
  readonly depth: number
  readonly context: RuntimePackageMappingContext
}

interface BuildAssemblyNodeInput {
  readonly assemblyFileId: string
  readonly displayNameOverride?: string
  readonly instanceUuid?: string
  readonly localTransform: readonly number[]
  readonly nodeKey: string
  readonly parentNodeKey?: string
  readonly depth: number
  readonly context: RuntimePackageMappingContext
}

/**
 * 判断未知 JSON 值是否为普通对象。
 *
 * @param value 待判断值。
 * @returns 是普通对象时返回 true。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * 读取字符串字段，缺失或类型不符时返回空字符串。
 *
 * @param value JSON 对象。
 * @param key 字段名。
 * @returns 去除首尾空白后的字符串。
 */
function readOptionalString(value: Record<string, unknown>, key: string): string {
  const fieldValue = value[key]
  return typeof fieldValue === 'string' ? fieldValue.trim() : ''
}

/**
 * 读取必须存在的字符串字段。
 *
 * @param value JSON 对象。
 * @param key 字段名。
 * @param context 错误定位上下文。
 * @returns 去除首尾空白后的字符串。
 */
function readRequiredString(value: Record<string, unknown>, key: string, context: string): string {
  const text = readOptionalString(value, key)
  if (!text) {
    throw new Error(`${context}: 缺少 ${key}`)
  }
  return text
}

/**
 * 读取 4x4 row-major transform。
 *
 * @param value transform 字段值。
 * @param context 错误定位上下文。
 * @returns 可供 cad-core 消费的 16 位矩阵。
 */
function readTransform(value: unknown, context: string): readonly number[] {
  if (!Array.isArray(value) || value.length !== 16 || value.some((item) => typeof item !== 'number')) {
    throw new Error(`${context}: transform 必须是 16 个数字`)
  }

  return [...value]
}

/**
 * 读取子节点数组。
 *
 * @param value children 字段值。
 * @param context 错误定位上下文。
 * @returns 子节点 JSON 数组。
 */
function readChildren(value: unknown, context: string): readonly AssemblyChildJson[] {
  if (value === undefined) {
    return []
  }
  if (!Array.isArray(value)) {
    throw new Error(`${context}: children 必须是数组`)
  }

  return value.map((child, index) => {
    if (!isRecord(child)) {
      throw new Error(`${context}: children[${index}] 必须是对象`)
    }
    return child as unknown as AssemblyChildJson
  })
}

/**
 * 归一化 zip 内部路径，支持相对路径中的 `.` 和 `..`。
 *
 * @param value zip entry 或 `_assembly.json` 中的文件引用。
 * @returns 归一化后的包内路径。
 */
function normalizeRuntimePackagePath(value: string): string {
  const normalizedValue = value.trim().replace(/\\/g, '/').replace(/^\/+/, '')
  const segments: string[] = []
  for (const segment of normalizedValue.split('/')) {
    if (!segment || segment === '.') {
      continue
    }
    if (segment === '..') {
      segments.pop()
      continue
    }
    segments.push(segment)
  }
  return segments.join('/')
}

/**
 * 获取包内路径的父目录。
 *
 * @param fileId 包内路径。
 * @returns 父目录；根目录文件返回空字符串。
 */
function getRuntimePackageDirname(fileId: string): string {
  const normalizedFileId = normalizeRuntimePackagePath(fileId)
  const lastSlashIndex = normalizedFileId.lastIndexOf('/')
  return lastSlashIndex >= 0 ? normalizedFileId.slice(0, lastSlashIndex) : ''
}

/**
 * 按声明顺序去重候选包内路径。
 *
 * @param candidates 候选路径列表。
 * @returns 去重后的候选路径。
 */
function uniqueRuntimePackageReferenceCandidates(candidates: readonly string[]): readonly string[] {
  const seen = new Set<string>()
  const result: string[] = []

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeRuntimePackagePath(candidate)
    if (!normalizedCandidate || seen.has(normalizedCandidate)) {
      continue
    }

    seen.add(normalizedCandidate)
    result.push(normalizedCandidate)
  }

  return result
}

/**
 * 生成 `_assembly.json` 中文件引用的候选包内路径。
 *
 * 设计说明：真实装配包里 `nested_assembly_json` 有时保存 `assemblies/X/_assembly.json`，有时保存
 * `../X/_assembly.json`，也可能只保存 `X/_assembly.json`。这里用 zip entry 存在性决定最终路径，
 * 避免把子装配错误拼到当前装配目录下面。
 *
 * @param currentAssemblyFileId 当前 assembly manifest 的包内路径。
 * @param referenceFileId 被引用的包内路径。
 * @param preferredRoot 省略根目录前缀时优先尝试的包根目录。
 * @returns 按优先级排列的候选路径。
 */
function createRuntimePackageReferenceCandidates(
  currentAssemblyFileId: string,
  referenceFileId: string,
  preferredRoot: RuntimePackageReferenceRoot,
): readonly string[] {
  const currentAssemblyDirectory = getRuntimePackageDirname(currentAssemblyFileId)
  const parentAssemblyDirectory = getRuntimePackageDirname(currentAssemblyDirectory)
  const normalizedReference = normalizeRuntimePackagePath(referenceFileId)

  return uniqueRuntimePackageReferenceCandidates([
    normalizedReference,
    `${currentAssemblyDirectory}/${referenceFileId}`,
    `${preferredRoot}/${referenceFileId}`,
    `${parentAssemblyDirectory}/${referenceFileId}`,
  ])
}

/**
 * 解析 `_assembly.json` 中的文件引用。
 *
 * @param entries zip entry 字节索引。
 * @param currentAssemblyFileId 当前 assembly manifest 的包内路径。
 * @param referenceFileId 被引用的包内路径。
 * @param preferredRoot 省略根目录前缀时优先尝试的包根目录。
 * @param context 错误定位上下文。
 * @returns 可直接读取 zip entry 的包内路径。
 */
function resolveRuntimePackageReference(
  entries: ReadonlyMap<string, Uint8Array>,
  currentAssemblyFileId: string,
  referenceFileId: string,
  preferredRoot: RuntimePackageReferenceRoot,
  context: string,
): string {
  const candidates = createRuntimePackageReferenceCandidates(
    currentAssemblyFileId,
    referenceFileId,
    preferredRoot,
  )
  const matchedCandidate = candidates.find((candidate) => entries.has(candidate))

  if (matchedCandidate) {
    return matchedCandidate
  }

  throw new Error(
    `${context}: 包内引用不存在 reference=${referenceFileId}, candidates=${candidates.join(', ')}`,
  )
}

/**
 * 构建 zip entry 字节索引。
 *
 * @param files fflate 解压得到的 entry 映射。
 * @returns 按归一化路径索引的字节表。
 */
function createRuntimePackageEntryMap(files: Record<string, Uint8Array>): ReadonlyMap<string, Uint8Array> {
  const entries = new Map<string, Uint8Array>()
  for (const [entryPath, entryBytes] of Object.entries(files)) {
    const normalizedEntryPath = normalizeRuntimePackagePath(entryPath)
    if (!normalizedEntryPath || normalizedEntryPath.endsWith('/')) {
      continue
    }
    entries.set(normalizedEntryPath, new Uint8Array(entryBytes))
  }
  return entries
}

/**
 * 读取包内文件字节。
 *
 * @param entries zip entry 字节索引。
 * @param fileId 包内路径。
 * @param context 错误定位上下文。
 * @returns 文件字节副本。
 */
function readRuntimePackageEntry(
  entries: ReadonlyMap<string, Uint8Array>,
  fileId: string,
  context: string,
): Uint8Array {
  const normalizedFileId = normalizeRuntimePackagePath(fileId)
  const bytes = entries.get(normalizedFileId)
  if (!bytes) {
    throw new Error(`${context}: 包内文件不存在 fileId=${normalizedFileId}`)
  }

  return new Uint8Array(bytes)
}

/**
 * 读取并解析包内 JSON。
 *
 * @param entries zip entry 字节索引。
 * @param fileId 包内路径。
 * @param context 错误定位上下文。
 * @returns 解析后的 JSON 对象。
 */
function readRuntimePackageJson(
  entries: ReadonlyMap<string, Uint8Array>,
  fileId: string,
  context: string,
): Record<string, unknown> {
  const bytes = readRuntimePackageEntry(entries, fileId, context)
  const text = new TextDecoder().decode(bytes)
  const parsed = JSON.parse(text) as unknown
  if (!isRecord(parsed)) {
    throw new Error(`${context}: JSON 根节点必须是对象 fileId=${fileId}`)
  }

  return parsed
}

/**
 * 从调试字段读取真实文件 UUID。
 *
 * @param debug `_assembly.json` child.debug 字段。
 * @returns 可用 UUID；缺失时返回空字符串。
 */
function readDebugFileUuid(debug: unknown): string {
  if (!isRecord(debug)) {
    return ''
  }

  const fileUuid = readOptionalString(debug, 'file_uuid') || readOptionalString(debug, 'fileUuid')
  return fileUuid
}

/**
 * 构建稳定 nodeKey 片段。
 *
 * @param name 组件实例名。
 * @param siblingIndex 同级序号。
 * @returns 与现有项目模型 key 策略兼容的编码片段。
 */
function encodeNodeKeySegment(name: string, siblingIndex: number): string {
  return encodeURIComponent(`${name}#${siblingIndex}`)
}

/**
 * 解析节点显示名。
 *
 * @param preferredName 首选名称。
 * @param fallbackName 兜底名称。
 * @param siblingIndex 同级序号。
 * @returns 非空显示名。
 */
function resolveNodeDisplayName(preferredName: string, fallbackName: string, siblingIndex: number): string {
  return preferredName.trim() || fallbackName.trim() || `node-${siblingIndex}`
}

/**
 * 创建 cad-core 可消费的文件描述。
 *
 * @param fileId 包内路径。
 * @param role 文件在加载计划中的职责。
 * @param bytes 文件字节。
 * @param kind 文件类型。
 * @param uuid 文件业务身份。
 * @returns 模型资产文件描述。
 */
function createRuntimePackageModelAssetFile(
  fileId: string,
  role: ModelAssetFile['role'],
  bytes: Uint8Array,
  kind: string,
  uuid: string,
): ModelAssetFile {
  return {
    fileId,
    uuid: uuid || `insert-runtime:${fileId}`,
    kind,
    size: BigInt(bytes.byteLength),
    url: `insert-runtime://${fileId}`,
    role,
  }
}

/**
 * 创建模型资产节点。
 *
 * @param input 节点字段。
 * @returns cad-core 模型资产节点。
 */
function createRuntimePackageModelAssetNode(input: {
  readonly nodeKey: string
  readonly displayName: string
  readonly instanceUuid?: string
  readonly parentNodeKey?: string
  readonly depth: number
  readonly nodeType: ModelAssetNode['nodeType']
  readonly localTransform: readonly number[]
  readonly assemblyManifest?: ModelAssetFile
  readonly packageFiles: readonly ModelAssetFile[]
  readonly children: readonly ModelAssetNode[]
  readonly sourceFileId: string
}): ModelAssetNode {
  return {
    nodeKey: input.nodeKey,
    displayName: input.displayName,
    instanceUuid: input.instanceUuid,
    parentNodeKey: input.parentNodeKey,
    depth: input.depth,
    repositoryId: EMPTY_REPOSITORY_ID,
    branchId: EMPTY_BRANCH_ID,
    commitId: EMPTY_COMMIT_ID,
    versionKey: `${normalizeInsertedCadFileId(input.sourceFileId)}::${input.nodeKey}`,
    versionSwitchable: false,
    nodeType: input.nodeType,
    localTransform: [...input.localTransform],
    boundingBox: [],
    assemblyManifest: input.assemblyManifest,
    packageFiles: input.packageFiles,
    children: input.children,
  }
}

/**
 * 将 assembly child 转换成模型资产节点。
 *
 * @param input 构建上下文。
 * @returns 模型资产节点。
 */
function buildRuntimePackageChildNode(input: BuildChildNodeInput): ModelAssetNode {
  const childRecord = input.child as unknown as Record<string, unknown>
  const nodeType = readRequiredString(childRecord, 'node_type', 'insert-cad-runtime-package child')
  const instanceName = readOptionalString(childRecord, 'instance_name')
  const nodeId = readOptionalString(childRecord, 'node_id')
  const instanceId = readOptionalString(childRecord, 'instance_id')
  const displayName = resolveNodeDisplayName(instanceName, nodeId || instanceId, input.childIndex)
  const nodeKey = `${input.parentNodeKey}/${encodeNodeKeySegment(displayName, input.childIndex)}`
  const localTransform = readTransform(input.child.transform, `insert-cad-runtime-package child ${displayName}`)

  if (nodeType === 'assembly') {
    const nestedAssemblyJson = readOptionalString(childRecord, 'nested_assembly_json')
    if (nestedAssemblyJson) {
      const nestedAssemblyFileId = resolveRuntimePackageReference(
        input.context.entries,
        input.currentAssemblyFileId,
        nestedAssemblyJson,
        'assemblies',
        'insert-cad-runtime-package nested assembly',
      )
      return buildRuntimePackageAssemblyNode({
        assemblyFileId: nestedAssemblyFileId,
        displayNameOverride: displayName,
        instanceUuid: instanceId,
        localTransform,
        nodeKey,
        parentNodeKey: input.parentNodeKey,
        depth: input.depth,
        context: input.context,
      })
    }

    const inlineChildren = readChildren(input.child.children, `insert-cad-runtime-package child ${displayName}`)
    return createRuntimePackageModelAssetNode({
      nodeKey,
      displayName,
      instanceUuid: instanceId || undefined,
      parentNodeKey: input.parentNodeKey,
      depth: input.depth,
      nodeType: 'assembly',
      localTransform,
      packageFiles: [],
      children: inlineChildren.map((inlineChild, inlineIndex) => buildRuntimePackageChildNode({
        child: inlineChild,
        childIndex: inlineIndex,
        currentAssemblyFileId: input.currentAssemblyFileId,
        parentNodeKey: nodeKey,
        depth: input.depth + 1,
        context: input.context,
      })),
      sourceFileId: input.context.sourceFileId,
    })
  }

  if (nodeType !== 'part') {
    throw new Error(`insert-cad-runtime-package: 未知节点类型 ${nodeType}`)
  }

  const nodeWhere = readOptionalString(childRecord, 'node_where')
  const packageFiles = nodeWhere
    ? [createRuntimePackageNodePackageFile({
      context: input.context,
      currentAssemblyFileId: input.currentAssemblyFileId,
      nodeWhere,
      debug: input.child.debug,
    })]
    : []

  return createRuntimePackageModelAssetNode({
    nodeKey,
    displayName,
    instanceUuid: instanceId || undefined,
    parentNodeKey: input.parentNodeKey,
    depth: input.depth,
    nodeType: 'part',
    localTransform,
    packageFiles,
    children: [],
    sourceFileId: input.context.sourceFileId,
  })
}

/**
 * 创建零件节点包文件描述，并校验引用的 `.step.puppy` 确实存在。
 *
 * @param input 当前子节点引用。
 * @returns 节点包文件描述。
 */
function createRuntimePackageNodePackageFile(input: {
  readonly context: RuntimePackageMappingContext
  readonly currentAssemblyFileId: string
  readonly nodeWhere: string
  readonly debug: unknown
}): ModelAssetFile {
  const packageFileId = resolveRuntimePackageReference(
    input.context.entries,
    input.currentAssemblyFileId,
    input.nodeWhere,
    'parts',
    'insert-cad-runtime-package node package',
  )
  const bytes = readRuntimePackageEntry(
    input.context.entries,
    packageFileId,
    'insert-cad-runtime-package node package',
  )

  return createRuntimePackageModelAssetFile(
    packageFileId,
    'node-package',
    bytes,
    NODE_PACKAGE_KIND,
    readDebugFileUuid(input.debug),
  )
}

/**
 * 读取并转换一个 assembly manifest。
 *
 * @param input assembly 构建输入。
 * @returns 模型资产 assembly 节点。
 */
function buildRuntimePackageAssemblyNode(input: BuildAssemblyNodeInput): ModelAssetNode {
  const assemblyFileId = normalizeRuntimePackagePath(input.assemblyFileId)
  const document = readRuntimePackageJson(
    input.context.entries,
    assemblyFileId,
    'insert-cad-runtime-package assembly',
  ) as unknown as AssemblyDocumentJson
  const documentRecord = document as unknown as Record<string, unknown>
  const productName = readOptionalString(documentRecord, 'product_name')
  const nodeId = readOptionalString(documentRecord, 'node_id')
  const displayName = resolveNodeDisplayName(input.displayNameOverride ?? productName, nodeId, 0)
  const assemblyBytes = readRuntimePackageEntry(
    input.context.entries,
    assemblyFileId,
    'insert-cad-runtime-package assembly',
  )
  const assemblyManifest = createRuntimePackageModelAssetFile(
    assemblyFileId,
    'assembly-manifest',
    assemblyBytes,
    ASSEMBLY_MANIFEST_KIND,
    `insert-runtime:${assemblyFileId}`,
  )
  const children = readChildren(document.children, `insert-cad-runtime-package assembly ${displayName}`)
    .map((child, childIndex) => buildRuntimePackageChildNode({
      child,
      childIndex,
      currentAssemblyFileId: assemblyFileId,
      parentNodeKey: input.nodeKey,
      depth: input.depth + 1,
      context: input.context,
    }))

  return createRuntimePackageModelAssetNode({
    nodeKey: input.nodeKey,
    displayName,
    instanceUuid: input.instanceUuid,
    parentNodeKey: input.parentNodeKey,
    depth: input.depth,
    nodeType: 'assembly',
    localTransform: input.localTransform,
    assemblyManifest,
    packageFiles: [],
    children,
    sourceFileId: input.context.sourceFileId,
  })
}

/**
 * 将 `.part.cad` 的 synthetic root 折叠为真正的零件根节点。
 *
 * @param input 解析上下文。
 * @param rootAssemblyFileId synthetic root assembly 文件路径。
 * @param rootDocument synthetic root 文档。
 * @returns 零件根节点。
 */
function buildFoldedPartRoot(
  input: ParseInsertedCadRuntimePackageInput,
  rootAssemblyFileId: string,
  rootDocument: AssemblyDocumentJson,
  context: RuntimePackageMappingContext,
): ModelAssetNode {
  const children = readChildren(rootDocument.children, 'insert-cad-runtime-package part root')
  if (children.length !== 1) {
    throw new Error(`insert-cad-runtime-package: .part.cad 必须包含唯一零件根节点，实际数量=${children.length}`)
  }

  const child = children[0]
  const childRecord = child as unknown as Record<string, unknown>
  const nodeType = readRequiredString(childRecord, 'node_type', 'insert-cad-runtime-package part root')
  if (nodeType !== 'part') {
    throw new Error(`insert-cad-runtime-package: .part.cad 根节点必须是 part，实际类型=${nodeType}`)
  }

  const nodeWhere = readRequiredString(childRecord, 'node_where', 'insert-cad-runtime-package part root')
  const displayName = input.displayName.trim()
    || readOptionalString(childRecord, 'instance_name')
    || readOptionalString(childRecord, 'node_id')
    || 'part'
  const nodeKey = encodeNodeKeySegment(displayName, 0)
  const localTransform = readTransform(child.transform, `insert-cad-runtime-package part root ${displayName}`)

  return createRuntimePackageModelAssetNode({
    nodeKey,
    displayName,
    instanceUuid: readOptionalString(childRecord, 'instance_id') || undefined,
    depth: 0,
    nodeType: 'part',
    localTransform,
    packageFiles: [createRuntimePackageNodePackageFile({
      context,
      currentAssemblyFileId: rootAssemblyFileId,
      nodeWhere,
      debug: child.debug,
    })],
    children: [],
    sourceFileId: input.fileId,
  })
}

/**
 * 创建从外层 zip 内存读取文件的 ModelAssetReader。
 *
 * @param entries zip entry 字节索引。
 * @returns cad-core 项目模型加载 reader。
 */
function createRuntimePackageModelAssetReader(entries: ReadonlyMap<string, Uint8Array>): ModelAssetReader {
  return {
    async readAll(file) {
      return readRuntimePackageEntry(entries, file.fileId, 'insert-cad-runtime-package reader')
    },
    async readRange(file, range) {
      const bytes = readRuntimePackageEntry(entries, file.fileId, 'insert-cad-runtime-package reader')
      return bytes.slice(range.start, range.end + 1)
    },
  }
}

/**
 * 解压插入 CAD 外层运行时包。
 *
 * @param bytes `/download/file` 返回的包字节。
 * @returns 包内 entry 字节索引。
 */
async function unzipInsertedCadRuntimePackage(bytes: Uint8Array): Promise<ReadonlyMap<string, Uint8Array>> {
  try {
    const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
    return createRuntimePackageEntryMap(unzipSync(bytes))
  } catch (error) {
    throw new Error(`insert-cad-runtime-package: 下载内容不是有效 zip 包，${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * 解析 `.part.cad` / `.assembly.cad` 下载包，并生成 cad-core 可消费的模型资产树。
 *
 * @param input 运行时包解析输入。
 * @returns 模型资产根节点和内存 reader。
 */
export async function parseInsertedCadRuntimePackage(
  input: ParseInsertedCadRuntimePackageInput,
): Promise<ParsedInsertedCadRuntimePackage> {
  const entries = await unzipInsertedCadRuntimePackage(input.bytes)
  const manifest = readRuntimePackageJson(
    entries,
    RUNTIME_MANIFEST_FILE_ID,
    'insert-cad-runtime-package manifest',
  ) as unknown as RuntimeManifestJson
  const manifestRecord = manifest as unknown as Record<string, unknown>
  const rootAssemblyFileId = normalizeRuntimePackagePath(
    readRequiredString(manifestRecord, 'root_assembly', 'insert-cad-runtime-package manifest'),
  )
  const rootDocument = readRuntimePackageJson(
    entries,
    rootAssemblyFileId,
    'insert-cad-runtime-package root assembly',
  ) as unknown as AssemblyDocumentJson
  const context: RuntimePackageMappingContext = {
    entries,
    sourceFileId: input.fileId,
  }
  const root = input.kind === 'part'
    ? buildFoldedPartRoot(input, rootAssemblyFileId, rootDocument, context)
    : buildRuntimePackageAssemblyNode({
      assemblyFileId: rootAssemblyFileId,
      displayNameOverride: undefined,
      localTransform: IDENTITY_TRANSFORM,
      nodeKey: encodeNodeKeySegment(
        readOptionalString(rootDocument as unknown as Record<string, unknown>, 'product_name')
          || input.displayName
          || 'assembly',
        0,
      ),
      depth: 0,
      context,
    })

  return {
    root,
    reader: createRuntimePackageModelAssetReader(entries),
  }
}
